import { useRef } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Stack,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { Container } from './Style';
import { useFetch } from '../../hooks/useFetch';
import LoadingContent from '../../components/LoadingContent';
import { getServerNow } from '../../hooks/useServerTime';

const LiveCard = ({ meeting, schedule }) => {
    const navigate = useNavigate();
    const now = getServerNow();
    const startTime = new Date(schedule.datetime);
    const minutesAgo = Math.max(0, Math.floor((now.getTime() - startTime.getTime()) / 60000));
    const conviva = schedule.Order?.[0]?.user;
    const host = meeting.Profile?.User;

    // Anfitriao e participante entram na sala; os demais assistem. Antes todo
    // clique caia na transmissao, inclusive o do sorteado e o do anfitriao.
    const pedidoDeEntrada = meeting.is_host
        ? schedule.host_order_id
        : schedule.my_order_id && (!meeting.raffle || schedule.my_order_chosen)
            ? schedule.my_order_id
            : null;

    const abrir = () =>
        navigate(pedidoDeEntrada ? `/encontros/${pedidoDeEntrada}/chamada` : `/lista/ao-vivo/${schedule.id}`);

    return (
        <Box
            sx={{
                width: '100%',
                bgcolor: 'white',
                borderRadius: '16px',
                border: '1px solid #e8e8e8',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                    borderColor: '#2e7d32',
                    boxShadow: '0 4px 20px rgba(46,125,50,0.12)',
                    transform: 'translateY(-2px)',
                },
            }}
            onClick={abrir}
        >
            {/* Barra superior ao vivo */}
            <Box sx={{
                bgcolor: '#2e7d32',
                px: 2,
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                    <FiberManualRecordIcon sx={{
                        fontSize: 8,
                        color: 'white',
                        animation: 'pulse 1.5s infinite',
                        '@keyframes pulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.3 },
                        },
                    }} />
                    <Typography variant="caption" sx={{ color: 'white', fontWeight: 600, fontSize: '0.7rem', letterSpacing: 0.5 }}>
                        AO VIVO
                    </Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.65rem' }}>
                    {minutesAgo > 0 ? `Iniciou há ${minutesAgo} min` : 'Iniciando agora'}
                </Typography>
            </Box>

            <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                {/* Titulo */}
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, lineHeight: 1.3 }}>
                    {meeting.title}
                </Typography>

                {/* Tags de assunto */}
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                    {meeting.topic && (
                        <Chip label={meeting.topic} size="small" sx={{ bgcolor: '#e6f3f1', color: '#006a61', fontWeight: 500, fontSize: '0.7rem', height: 22 }} />
                    )}
                    {meeting.subtopic && (
                        <Chip label={meeting.subtopic} size="small" variant="outlined" sx={{ borderColor: '#ccc', color: '#666', fontSize: '0.7rem', height: 22 }} />
                    )}
                </Stack>

                {/* Participantes */}
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{
                    bgcolor: '#fafafa',
                    borderRadius: '12px',
                    p: 1.5,
                    mb: 2,
                }}>
                    {/* Anfitriao */}
                    <Avatar
                        src={host?.profileImage}
                        sx={{ width: 36, height: 36, border: '2px solid #006a61', flexShrink: 0 }}
                    >
                        {host?.name?.[0]}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', lineHeight: 1 }}>
                            Anfitrião
                        </Typography>
                        <Typography variant="body2" fontWeight={500} noWrap sx={{ fontSize: '0.8rem' }}>
                            {host?.name || 'Anfitriao'}
                        </Typography>
                    </Box>

                    {/* Divisor */}
                    <Box sx={{ width: '1px', height: 32, bgcolor: '#e0e0e0', flexShrink: 0 }} />

                    {/* Conviva */}
                    <Avatar
                        src={conviva?.profileImage}
                        sx={{ width: 36, height: 36, border: '2px solid #1976d2', flexShrink: 0 }}
                    >
                        {conviva?.name?.[0] || '?'}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', lineHeight: 1 }}>
                            Conviva
                        </Typography>
                        <Typography variant="body2" fontWeight={500} noWrap sx={{ fontSize: '0.8rem' }}>
                            {conviva?.name || 'Aguardando'}
                        </Typography>
                    </Box>
                </Stack>

                {/* Botao assistir */}
                <Button
                    fullWidth
                    variant="contained"
                    startIcon={<VisibilityIcon />}
                    sx={{
                        bgcolor: '#2e7d32',
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        height: 40,
                        '&:hover': { bgcolor: '#1b5e20' },
                    }}
                >
                    {pedidoDeEntrada ? 'Entrar no encontro' : 'Assistir como espectador'}
                </Button>
            </Box>
        </Box>
    );
};

const LiveMeetings = () => {
    const navigate = useNavigate();
    const prevRef = useRef(null);
    const nextRef = useRef(null);
    const { data, isLoading } = useFetch('/search/live');

    // Achatar: cada meeting pode ter múltiplos schedules ao vivo
    const liveItems = (data || []).flatMap(meeting =>
        (meeting.meeting_schedule || []).map(schedule => ({ meeting, schedule }))
    );

    return (
        <Container>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/lista')}
                sx={{ textTransform: 'none', color: '#666', alignSelf: 'flex-start' }}
            >
                Voltar para encontros
            </Button>

            <Box sx={{ width: '90%', mt: 4, '@media (max-width: 750px)': { width: '100%', mt: 2 } }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 400, fontSize: '1.5rem', lineHeight: 1.334, whiteSpace: 'nowrap' }}>
                        Acontecendo agora
                    </Typography>
                    {!isLoading && (
                        <Chip
                            size="small"
                            label={`${liveItems.length} ao vivo`}
                            sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.7rem' }}
                        />
                    )}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Encontros públicos acontecendo neste momento. Assista ao vivo como espectador.
                </Typography>

                {isLoading ? (
                    <LoadingContent />
                ) : liveItems.length === 0 ? (
                    <Alert severity="info" sx={{ borderRadius: '12px' }}>
                        Nenhum encontro acontecendo no momento. Volte mais tarde!
                    </Alert>
                ) : (
                    <Box sx={{ position: 'relative' }}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: { xs: 'block', sm: 'none' }, mb: 1, textAlign: 'center', fontStyle: 'italic' }}
                        >
                            Arraste para os lados para navegar
                        </Typography>
                        <Box
                            ref={prevRef}
                            sx={{
                                position: 'absolute', top: '45%', left: -40, zIndex: 10,
                                transform: 'translateY(-50%)', cursor: 'pointer', opacity: 0.5,
                                '&:hover': { opacity: 1 },
                                display: { xs: 'none', sm: 'block' },
                            }}
                        >
                            <ArrowBackIosIcon />
                        </Box>
                        <Box
                            ref={nextRef}
                            sx={{
                                position: 'absolute', top: '45%', right: -40, zIndex: 10,
                                transform: 'translateY(-50%)', cursor: 'pointer', opacity: 0.5,
                                '&:hover': { opacity: 1 },
                                display: { xs: 'none', sm: 'block' },
                            }}
                        >
                            <ArrowForwardIosIcon />
                        </Box>
                        <Swiper
                            modules={[Navigation]}
                            spaceBetween={16}
                            slidesPerView={1}
                            breakpoints={{
                                600: { slidesPerView: 1.5 },
                                900: { slidesPerView: 2.5 },
                                1200: { slidesPerView: 3 },
                            }}
                            navigation={{
                                prevEl: prevRef.current,
                                nextEl: nextRef.current,
                            }}
                            onBeforeInit={(swiper) => {
                                if (typeof swiper.params.navigation !== 'boolean') {
                                    swiper.params.navigation.prevEl = prevRef.current;
                                    swiper.params.navigation.nextEl = nextRef.current;
                                }
                            }}
                            style={{ padding: '4px 0' }}
                        >
                            {liveItems.map(({ meeting, schedule }) => (
                                <SwiperSlide key={`${meeting.id}-${schedule.id}`}>
                                    <LiveCard meeting={meeting} schedule={schedule} />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </Box>
                )}
            </Box>
        </Container>
    );
};

export default LiveMeetings;
